import {
  ActionRef,
  ActionTransactionSubType,
  ActionType,
} from 'further-types/lp/action';
import {
  CurrencyRateDetails,
  TransactionOperation,
} from 'further-types/lp/transaction';
import { CurrencyCode } from 'further-types/lp/vehicle';
import api from 'lib/trpcClient';

type Transaction = {
  id?: string;
  actionId?: string;
  transactionTypeId: string;
  operationType: TransactionOperation;
  amountExVatLcy?: number | null;
  amountIncVatLcy?: number | null;
  amountExVatTcy: number;
  amountIncVatTcy: number;
  transactionCurrencyRate?: CurrencyRateDetails;
  transactionCurrency: CurrencyCode;
  transactionSubType: ActionTransactionSubType;
};

type Props = {
  vehicleId: string;
  action: {
    actionRef: ActionRef;
    actionType: ActionType;
    customActionTypeId?: string;
    amountIncVatTcy: number;
    amountExVatTcy: number;
    currency: CurrencyCode;
  };
  transactions?: Array<Transaction>;
};

export default function useActionTransactions({
  vehicleId,
  action: {
    actionRef,
    actionType,
    customActionTypeId,
    amountIncVatTcy,
    amountExVatTcy,
    currency,
  },
  transactions,
}: Props): Array<Transaction> {
  const [defaultTransactionTypes] =
    api.action.getTransactionTypesForAction.useSuspenseQuery({
      vehicleId,
      actionRef,
      actionType,
      customActionTypeId,
      amountIncVatTcy,
      amountExVatTcy,
    });

  const preservedTransactions = (transactions ?? []).map((transaction) => {
    const defaultType = defaultTransactionTypes?.find(
      (t) =>
        t.operationType === transaction.operationType &&
        t.transactionSubType === transaction.transactionSubType,
    );

    return {
      ...transaction,
      ...(defaultType || {}),
      id: transaction.id,
      actionId: transaction.actionId,
      transactionTypeId: transaction.transactionTypeId,
      operationType: transaction.operationType,
      amountExVatTcy,
      amountIncVatTcy,
      amountExVatLcy: transaction.amountExVatLcy,
      amountIncVatLcy: transaction.amountIncVatLcy,
      transactionCurrency: currency,
      transactionCurrencyRate: transaction.transactionCurrencyRate,
      currencyCode: currency,
    };
  });

  // Create a set of existing transaction keys to avoid duplicates
  const existingTransactionKeys = new Set(
    preservedTransactions.map(
      (t) => `${t.operationType}-${t.transactionSubType}`,
    ),
  );

  // Add default transactions that don't already exist
  const additionalDefaultTransactions =
    defaultTransactionTypes
      ?.filter(
        (defaultType) =>
          !existingTransactionKeys.has(
            `${defaultType.operationType}-${defaultType.transactionSubType}`,
          ),
      )
      .map((transaction) => ({
        ...transaction,
        amountIncVatTcy: transaction.amountIncVatTcy,
        amountExVatTcy: transaction.amountExVatTcy,
        transactionCurrency: currency,
        operationType: transaction.operationType,
        transactionSubType: transaction.transactionSubType,
      })) ?? [];

  // Merge preserved and additional default transactions
  return [...preservedTransactions, ...additionalDefaultTransactions];
}
